import React, { useState, useEffect } from "react";
import { Alert, Modal, Input, Button, message } from "antd";
import config from "../../config";

// Committed static guide in public/.
const GUIDE_URL = "/Marketplace_Registration_and_Installation_Guide.pdf";

const authHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const h = { "Content-Type": "application/json" };
  if (user && user.key) h.Authorization = "Token " + user.key;
  return h;
};

// Shows a reminder when the currently-selected office isn't connected to Sikka
// (no office_id). Lets the user connect inline. Renders nothing when connected,
// or when the practice registered with the PMS step switched off (in_onboarding
// false) — connecting Sikka wasn't part of what they were asked to do.
const SikkaReminderBanner = () => {
  const [connected, setConnected] = useState(true); // default: no banner until we know
  const [officeId, setOfficeId] = useState(localStorage.getItem("selectedOffice"));
  const [modalOpen, setModalOpen] = useState(false);
  const [sikkaId, setSikkaId] = useState("");
  const [loading, setLoading] = useState(false);

  const check = async (oid) => {
    if (!oid) return;
    try {
      const res = await fetch(
        config.apiGateway.URL + "/clinics/office-sikka-status/?office_id=" + oid,
        { headers: authHeaders() }
      );
      const json = await res.json();
      // An older backend sends no in_onboarding: treat it as part of onboarding.
      setConnected(!!json.sikka_connected || json.in_onboarding === false);
    } catch (e) {
      /* fail silent -> no banner */
    }
  };

  useEffect(() => {
    const oid = localStorage.getItem("selectedOffice");
    setOfficeId(oid);
    check(oid);
    const onStorage = () => {
      const next = localStorage.getItem("selectedOffice");
      if (next !== oid) {
        setOfficeId(next);
        check(next);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const submit = async () => {
    if (!sikkaId.trim()) {
      message.warning("Enter your office's PMS integration ID");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(config.apiGateway.URL + "/clinics/set-office-sikka/", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ offices: [{ office_id: officeId, sikka_id: sikkaId.trim() }] }),
      });
      const json = await res.json();
      setLoading(false);
      if (res.status < 250) {
        message.success("PMS integration connected");
        setConnected(true);
        setModalOpen(false);
      } else {
        message.error(
          (json.errors && json.errors[0] && json.errors[0].error) || "Couldn't connect the PMS integration"
        );
      }
    } catch (e) {
      setLoading(false);
      message.error("Couldn't connect the PMS integration");
    }
  };

  if (connected) return null;

  return (
    <>
      <Alert
        type="warning"
        banner
        showIcon
        message={
          <span>
            Connect this office's PMS integration to sync your practice data.{" "}
            <a href={GUIDE_URL} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
              Download the setup guide
            </a>
          </span>
        }
        action={
          <Button size="small" type="primary" onClick={() => setModalOpen(true)}>
            Connect PMS integration
          </Button>
        }
      />
      <Modal
        title="Connect this office's PMS integration"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        confirmLoading={loading}
        okText="Connect"
      >
        <p>Enter this office's PMS integration ID (from the welcome email you received when you registered for the integration).</p>
        <Input
          placeholder="Office PMS integration ID"
          value={sikkaId}
          onChange={(e) => setSikkaId(e.target.value)}
        />
        <p style={{ marginTop: 12 }}>
          <a href={GUIDE_URL} target="_blank" rel="noopener noreferrer">
            Need help? Download the setup guide (PDF)
          </a>
        </p>
      </Modal>
    </>
  );
};

export default SikkaReminderBanner;
